import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Faq, Service } from '@/types'

import Link from 'next/link'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PageHero } from '@/components/sections/page-hero'
import { SectionHeading } from '@/components/sections/section-heading'
import { ProcessSteps } from '@/components/sections/process-steps'
import { FaqList } from '@/components/sections/faq-list'
import { CtaBand } from '@/components/sections/cta-band'
import { CmsImage } from '@/components/cms/cms-image'
import { ServiceIcon } from '@/components/services/service-icon'

import {
    REVALIDATE_SECONDS,
    RETRY_SECONDS,
    getFaqs,
    getServiceIdsWithProjects,
    getServices,
    loadContent
} from '@/lib/content'

import { contactHref } from '@/constants/site'

interface ServicesPageProps {
    services: Array<Service>
    faqs: Array<Faq>
    serviceIdsWithProjects: Array<string>
}

const ServicesPage: FC<ServicesPageProps> = ({ services, faqs, serviceIdsWithProjects }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Usluge'
                description='Građevinski radovi po principu ključ u ruke, adaptacije i renoviranja te elektro i vodo instalacije u Sarajevu i okolini.'
            />

            <PageHero
                eyebrow='Naše usluge'
                title='Kompletna rješenja za građevinske radove'
                description='Izaberite jednu uslugu ili nam povjerite cijeli projekat, od pripremnih radova do primopredaje.'
            >
                {services.length > 0 && (
                    <nav aria-label='Brzi skok na uslugu' className='mt-9'>
                        <ul className='flex flex-wrap justify-center gap-2'>
                            {services.map(service => (
                                <li key={service.id}>
                                    <a
                                        href={`#${service.slug}`}
                                        className='inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-4 py-2 text-sm font-medium text-white/90 transition-colors outline-none hover:border-white/50 hover:bg-white/10 focus-visible:ring-3 focus-visible:ring-ring/50'
                                    >
                                        <ServiceIcon slug={service.slug} className='size-4 text-highlight' />
                                        <span data-mdlfy-cms={`services:${service.id}:title`}>
                                            {service.title}
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                )}
            </PageHero>

            {services.length === 0 && (
                <section id='usluge-uskoro' className='py-20'>
                    <Container className='max-w-2xl text-center'>
                        <h2 className='text-3xl font-bold tracking-tight'>
                            Popis usluga uskoro ažuriramo
                        </h2>

                        <p className='mt-4 text-lg leading-relaxed text-muted-foreground'>
                            Pošaljite nam upit i rado ćemo Vam reći kako Vam možemo pomoći.
                        </p>

                        <Link href={contactHref} className={buttonVariants({ size: 'lg', className: 'mt-8' })}>
                            Pošaljite upit
                        </Link>
                    </Container>
                </section>
            )}

            {services.map((service, index) => {
                const reversed = index % 2 === 1
                const hasProjects = serviceIdsWithProjects.includes(service.id)

                return (
                    <section
                        key={service.id}
                        id={service.slug}
                        aria-labelledby={`${service.slug}-title`}
                        className={cn('py-16 lg:py-20', reversed && 'bg-secondary')}
                    >
                        <Container className='grid items-center gap-10 lg:grid-cols-2 lg:gap-16'>
                            <div className={cn('relative aspect-[4/3] overflow-hidden rounded-md bg-muted', reversed && 'lg:order-2')}>
                                <CmsImage
                                    src={service.image}
                                    alt={service.image_alt}
                                    sizes='(min-width: 1280px) 592px, (min-width: 1024px) 50vw, 100vw'
                                    preload={index === 0}
                                />
                            </div>

                            <div>
                                <ServiceIcon slug={service.slug} className='size-11 text-primary' />

                                <h2 id={`${service.slug}-title`} className='mt-5 text-3xl font-bold tracking-tight sm:text-4xl'>
                                    <span data-mdlfy-cms={`services:${service.id}:title`}>
                                        {service.title}
                                    </span>
                                </h2>

                                {service.summary && (
                                    <p className='mt-4 text-lg leading-relaxed text-foreground/90' data-mdlfy-cms={`services:${service.id}:summary`}>
                                        {service.summary}
                                    </p>
                                )}

                                {service.description && (
                                    <p className='mt-4 leading-relaxed text-muted-foreground' data-mdlfy-cms={`services:${service.id}:description`}>
                                        {service.description}
                                    </p>
                                )}

                                {service.includes.length > 0 && (
                                    <div className='mt-8'>
                                        <h3 className='text-xs font-bold tracking-[0.16em] text-foreground uppercase'>
                                            Šta uključuje
                                        </h3>

                                        <ul className='mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2'>
                                            {service.includes.map(item => (
                                                <li key={item} className='flex items-start gap-3 text-sm leading-snug'>
                                                    <span className='mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'>
                                                        <Check className='size-3' aria-hidden='true' />
                                                    </span>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className='mt-9 flex flex-col gap-3 sm:flex-row'>
                                    <Link href={`${contactHref}?usluga=${encodeURIComponent(service.slug)}`} className={buttonVariants({ size: 'lg' })}>
                                        Zatražite ponudu
                                        <ArrowRight data-icon='inline-end' aria-hidden='true' />
                                    </Link>

                                    {hasProjects && (
                                        <Link href={`/projekti?usluga=${encodeURIComponent(service.slug)}`} className={buttonVariants({ size: 'lg', variant: 'outline' })}>
                                            Pogledajte projekte
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </Container>
                    </section>
                )
            })}

            <ProcessSteps />

            {faqs.length > 0 && (
                <section id='cesta-pitanja' aria-labelledby='cesta-pitanja-title' className='py-20 lg:py-24'>
                    <Container className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
                        <div className='lg:col-span-4'>
                            <SectionHeading
                                align='left'
                                eyebrow='Pitanja'
                                title='Česta pitanja'
                                description='Niste pronašli odgovor? Pošaljite nam upit i javit ćemo Vam se u najkraćem roku.'
                                headingId='cesta-pitanja-title'
                            />

                            <Link href={contactHref} className={buttonVariants({ variant: 'outline', size: 'lg', className: 'mt-8' })}>
                                Pošaljite upit
                            </Link>
                        </div>

                        <div className='lg:col-span-8'>
                            <FaqList faqs={faqs} />
                        </div>
                    </Container>
                </section>
            )}

            <CtaBand />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ServicesPageProps> = async () => {
    const [services, faqs, serviceIdsWithProjects] = await Promise.all([
        loadContent('services', getServices, []),
        loadContent('FAQs', getFaqs, []),
        loadContent('service project counts', getServiceIdsWithProjects, [])
    ])

    const failed = services.failed || faqs.failed || serviceIdsWithProjects.failed

    return {
        props: {
            services: services.data,
            faqs: faqs.data,
            serviceIdsWithProjects: serviceIdsWithProjects.data
        },
        revalidate: failed ? RETRY_SECONDS : REVALIDATE_SECONDS
    }
}

export default ServicesPage
